import { accessValue } from '../../../core/utils/accessors/access-value.js';
import type { ChartData } from '../../core/types/chart-data.js';
import { DEFAULT_TILE_NAME } from '../constants.js';
import type { HoneycombTileData } from '../types/honeycomb.js';

const DEFAULT_VALUE_ACCESSOR = 'value';

/**
 * Converts a unified `ChartData` array into the internal `HoneycombTileData[]`
 * shape consumed by the rendering pipeline.
 *
 * Each datapoint's value is resolved via `valueAccessor` (dot-notation
 * supported). The `name` field is read from the datapoint when present;
 * otherwise a default incremented name is generated.
 * Datapoints whose resolved value is `null` or `undefined` are skipped.
 *
 * @param data - the unified chart dataset
 * @param valueAccessor - field path to the tile value (defaults to `"value"`)
 * @returns the internal `HoneycombTileData` array
 * @internal
 */
export function normalizeHoneycombChartData(
  data: ChartData,
  valueAccessor: string = DEFAULT_VALUE_ACCESSOR,
): HoneycombTileData[] {
  const result: HoneycombTileData[] = [];

  data.forEach((datapoint, index) => {
    const rawValue = accessValue<string | number>(datapoint, valueAccessor);

    if (rawValue === null || rawValue === undefined) {
      return;
    }

    const nameValue = accessValue<unknown>(datapoint, 'name');
    const nameIndex = data.length > 1 ? index : undefined;
    const name =
      nameValue !== null && nameValue !== undefined
        ? String(nameValue)
        : nameIndex !== undefined
          ? `${DEFAULT_TILE_NAME}-${nameIndex + 1}`
          : DEFAULT_TILE_NAME;

    result.push({ ...datapoint, value: rawValue, name } as HoneycombTileData);
  });

  return result;
}
